/* /ios-share-setup — hybrid share-sheet + clipboard iOS Shortcut walkthrough.
 *
 * DESIGN NOTES
 * ─────────────
 * • Auth is Safari-cookie based. No token pastes; nothing per-user in the
 *   shortcut. If a user is logged in at recipefiler.com in Safari, the
 *   shortcut Just Works.
 * • The shortcut uses "Get URLs from Input" with the "If there's no input"
 *   fallback set to CLIPBOARD. That makes it work from BOTH the share sheet
 *   AND from a home-screen tap after Copy-Link (the reliable path for
 *   Instagram Reels which strip the share-sheet URL).
 * • When ios_shortcut_url_simple is populated in site-config, we hide the
 *   manual build and show a big install button. When empty, we show the
 *   4-action manual build.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShareNetwork, CheckCircle, Copy as CopyIcon, ShieldCheck, ArrowsClockwise,
  DownloadSimple, Lightning, Info, Envelope, TestTube, ClipboardText,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageMeta from "@/components/PageMeta";

// ─── SVG mocks ────────────────────────────────────────────────────────────────
const IOS_BG = "#F2F2F7";
const IOS_CARD = "#FFFFFF";
const IOS_BLUE = "#007AFF";
const IOS_PILL = "#5AC8FA";
const IOS_ORANGE = "#FF9500";
const IOS_TEXT = "#1C1C1E";
const IOS_MUTED = "#8E8E93";
const RF_ORANGE = "#F97316";

/* Mock A — the four actions stacked in the shortcut editor, with the key
   "If there's no input → Clipboard" sub-setting highlighted in orange. */
function MockActions() {
  return (
    <svg viewBox="0 0 320 340" className="w-full max-w-[280px] mx-auto" role="img" aria-label="Four actions stacked in the Shortcuts editor with the Clipboard fallback highlighted">
      <rect width="320" height="340" rx="24" fill={IOS_BG} />
      <text x="20" y="24" fontSize="11" fontWeight="700" fill={IOS_TEXT}>9:41</text>
      <text x="160" y="46" fontSize="14" fontWeight="800" textAnchor="middle" fill={IOS_TEXT}>Save to Recipe Filer</text>

      {/* 1. Get URLs from Input — with fallback highlighted */}
      <rect x="20" y="60" width="280" height="70" rx="12" fill={IOS_CARD} stroke={RF_ORANGE} strokeWidth="2" />
      <circle cx="42" cy="82" r="9" fill={IOS_BLUE} />
      <text x="42" y="86" fontSize="10" fontWeight="900" fill="#fff" textAnchor="middle">🔗</text>
      <text x="60" y="80" fontSize="11" fontWeight="800" fill={IOS_TEXT}>Get URLs from Input</text>
      <text x="60" y="94" fontSize="8.5" fill={IOS_MUTED}>from Shortcut Input</text>
      <rect x="60" y="102" width="230" height="22" rx="6" fill="#FFF3E0" stroke={RF_ORANGE} strokeWidth="1.5" strokeDasharray="3 2" />
      <text x="70" y="116" fontSize="9" fontWeight="800" fill={RF_ORANGE}>If no input:</text>
      <rect x="150" y="106" width="72" height="14" rx="7" fill={RF_ORANGE} />
      <text x="186" y="116" fontSize="9" fontWeight="900" fill="#fff" textAnchor="middle">Clipboard</text>
      <text x="238" y="116" fontSize="8.5" fill={IOS_MUTED}>← must set</text>

      {/* 2. URL Encode */}
      <rect x="20" y="138" width="280" height="42" rx="12" fill={IOS_CARD} stroke={IOS_MUTED} strokeOpacity="0.3" />
      <circle cx="42" cy="159" r="9" fill={IOS_BLUE} />
      <text x="42" y="163" fontSize="10" fontWeight="900" fill="#fff" textAnchor="middle">%</text>
      <text x="60" y="157" fontSize="10.5" fontWeight="800" fill={IOS_TEXT}>URL Encode</text>
      <rect x="60" y="164" width="90" height="13" rx="6.5" fill={IOS_PILL} />
      <text x="105" y="174" fontSize="8.5" fontWeight="800" fill="#fff" textAnchor="middle">URLs</text>

      {/* 3. Text */}
      <rect x="20" y="188" width="280" height="60" rx="12" fill={IOS_CARD} stroke={IOS_MUTED} strokeOpacity="0.3" />
      <circle cx="42" cy="210" r="9" fill={IOS_BLUE} />
      <text x="42" y="214" fontSize="10" fontWeight="900" fill="#fff" textAnchor="middle">Aa</text>
      <text x="60" y="208" fontSize="10.5" fontWeight="800" fill={IOS_TEXT}>Text</text>
      <text x="60" y="222" fontSize="7.5" fill={IOS_MUTED}>https://recipefiler.com/share-import?url=</text>
      <rect x="60" y="228" width="120" height="14" rx="7" fill={IOS_PILL} />
      <text x="120" y="238" fontSize="8.5" fontWeight="800" fill="#fff" textAnchor="middle">URL Encoded</text>

      {/* 4. Open URLs */}
      <rect x="20" y="256" width="280" height="38" rx="12" fill={IOS_CARD} stroke={IOS_MUTED} strokeOpacity="0.3" />
      <circle cx="42" cy="275" r="9" fill={IOS_ORANGE} />
      <text x="42" y="279" fontSize="10" fontWeight="900" fill="#fff" textAnchor="middle">↗</text>
      <text x="60" y="278" fontSize="10.5" fontWeight="800" fill={IOS_TEXT}>Open URLs</text>

      <text x="160" y="316" fontSize="10" fill={IOS_MUTED} textAnchor="middle">4 actions · works from Share Sheet</text>
      <text x="160" y="330" fontSize="10" fill={IOS_MUTED} textAnchor="middle">AND from Home Screen after Copy Link</text>
    </svg>
  );
}

/* Mock B — the Details ⓘ toggle for Share Sheet. */
function MockDetails() {
  return (
    <svg viewBox="0 0 320 220" className="w-full max-w-[280px] mx-auto" role="img" aria-label="Shortcut Details panel with Show in Share Sheet toggled on">
      <rect width="320" height="220" rx="24" fill={IOS_BG} />
      <text x="160" y="34" fontSize="13" fontWeight="800" textAnchor="middle" fill={IOS_TEXT}>Details</text>
      <text x="160" y="52" fontSize="9" fill={IOS_MUTED} textAnchor="middle">Save to Recipe Filer</text>
      <rect x="20" y="70" width="280" height="52" rx="14" fill={IOS_CARD} stroke={RF_ORANGE} strokeWidth="2" />
      <text x="34" y="103" fontSize="12" fontWeight="700" fill={IOS_TEXT}>Show in Share Sheet</text>
      <rect x="240" y="86" width="42" height="24" rx="12" fill="#34C759" />
      <circle cx="270" cy="98" r="10" fill="#fff" />
      <path d="M 250 130 L 250 138" stroke={RF_ORANGE} strokeWidth="2" strokeLinecap="round" />
      <path d="M 246 134 L 250 128 L 254 134 Z" fill={RF_ORANGE} />
      <text x="240" y="152" fontSize="9" fill={RF_ORANGE} fontWeight="800" textAnchor="middle">Must be ON</text>
      <rect x="20" y="164" width="280" height="42" rx="14" fill={IOS_CARD} stroke={IOS_MUTED} strokeOpacity="0.3" />
      <text x="34" y="190" fontSize="11" fill={IOS_MUTED}>Show in Search</text>
      <rect x="248" y="176" width="34" height="20" rx="10" fill="#E5E5EA" />
      <circle cx="257" cy="186" r="8" fill="#fff" />
    </svg>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function IOSShareSetup() {
  const { user, loading } = useAuth();
  const [origin, setOrigin] = useState("");
  const [shareToken, setShareToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [shortcutUrl, setShortcutUrl] = useState("");
  const [shortcutUrlSimple, setShortcutUrlSimple] = useState("");
  const [hideHybrid, setHideHybrid] = useState(false); // admin toggle: hide the Safari/clipboard method entirely
  const [showTokenPath, setShowTokenPath] = useState(true); // always show the token method — both flows sit on the page simultaneously when hybrid is also visible

  useEffect(() => { setOrigin(window.location.origin); }, []);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setShortcutUrl(r.data?.ios_shortcut_url || "");
        setShortcutUrlSimple(r.data?.ios_shortcut_url_simple || "");
        setHideHybrid(!!r.data?.hide_hybrid_ios_method);
      })
      .catch(() => {});
  }, []);

  // When the admin has hidden the hybrid method, the token path IS the tutorial
  // — auto-open it so users see it without hunting for a toggle.
  useEffect(() => {
    if (hideHybrid) setShowTokenPath(true);
  }, [hideHybrid]);

  useEffect(() => {
    if (loading || !user || !showTokenPath) return;
    setTokenLoading(true);
    api.get("/me/share-token")
      .then((r) => setShareToken(r.data.token || ""))
      .catch(() => setShareToken(""))
      .finally(() => setTokenLoading(false));
  }, [loading, user, showTokenPath]);

  const rotateToken = async () => {
    if (!confirm("Rotate your share token? Your existing iPhone shortcut will stop working until you re-paste the new token into it.")) return;
    setTokenLoading(true);
    try {
      const { data } = await api.post("/me/share-token/rotate");
      setShareToken(data.token);
      toast.success("Token rotated. Update your shortcut.");
    } catch {
      toast.error("Couldn't rotate. Try again in a moment.");
    } finally {
      setTokenLoading(false);
    }
  };

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Copy failed — long-press to copy ${label} manually`);
    }
  };

  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const apiEndpoint = `${origin}/api/share-import-token`;
  const apiEndpointText = `${origin}/api/share-import-token?format=text`;
  const safariEndpoint = `${origin}/share-import?url=`;
  const testShareUrl = `${origin}/share-import?url=https%3A%2F%2Fwww.bbcgoodfood.com%2Frecipes%2Fclassic-lasagne`;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
      <PageMeta
        title="iOS iPhone — Share Sheet & Shortcut Setup"
        description="Wire your iPhone Share Sheet to Recipe Filer with our free Apple Shortcut — save recipes from Safari, TikTok, Instagram or any app in one tap."
        canonical="/ios-share-setup"
        keywords="iPhone recipe shortcut, iOS Share Sheet recipe app, Apple Shortcut recipes, save recipes from Instagram iPhone, save TikTok recipe, save Safari recipe, Share to Recipe Filer, iPhone cookbook shortcut, one-tap save recipe iPhone"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Set up the Recipe Filer iOS Share Sheet shortcut",
          description: "Install a free Apple Shortcut so you can save recipes from Safari, Instagram, TikTok or Pinterest to Recipe Filer in one tap.",
          totalTime: "PT2M",
          step: [
            { "@type": "HowToStep", position: 1, name: "Install the Shortcut", text: "Tap the Install Shortcut link on your iPhone to add Recipe Filer's Share Sheet action." },
            { "@type": "HowToStep", position: 2, name: "Sign in", text: "Open the Shortcut once and paste your Recipe Filer API token to link your account." },
            { "@type": "HowToStep", position: 3, name: "Share to save", text: "In any app, tap the Share icon and choose 'Save to Recipe Filer' to import a recipe." },
          ],
        }}
      />

      {/* ── Hero ── */}
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center text-xs font-black uppercase tracking-[0.18em] text-rf-ink bg-rf-amber rounded-full px-3 py-1.5">iOS iPhone</span>
        <p className="overline" data-testid="ios-setup-overline">share-sheet setup</p>
      </div>
      <h1 className="font-display text-4xl md:text-6xl font-black text-rf-ink mb-6 leading-[0.95]">
        Save a recipe to<br />Recipe Filer<br /><span className="text-rf-orange">from anywhere on your iPhone.</span>
      </h1>
      <p className="text-rf-ink-soft text-lg leading-relaxed mb-10 max-w-2xl">
        A tiny Apple Shortcut wires your iPhone Share Sheet (and clipboard) to Recipe Filer.
        Install it once → every recipe on TikTok, Safari, BBC Good Food, Instagram, YouTube is one tap from your Inbox.
      </p>

      {!user && (
        <div className="card-recipe bg-rf-amber/15 border-rf-amber/40 mb-8" data-testid="ios-need-login">
          <p className="font-display font-extrabold text-rf-ink text-lg">Sign in first</p>
          <p className="text-rf-ink-soft text-sm mt-1">
            The shortcut recognises you via your Safari session — so please <Link to="/login?next=/ios-share-setup" className="text-rf-orange font-bold underline">sign in</Link> before installing.
          </p>
        </div>
      )}

      {!isIOS && (
        <div className="card-recipe bg-rf-amber/15 border-rf-amber/40 mb-8" data-testid="ios-non-ios-warn">
          <p className="font-display font-extrabold text-rf-ink text-lg">You&apos;re not on an iPhone or iPad</p>
          <p className="text-rf-ink-soft text-sm mt-1">This guide only applies to iOS devices. On Android, install Recipe Filer (Add to Home Screen) and we appear in the share sheet automatically.</p>
        </div>
      )}

      {/* ── One-tap install (only shown when we've hosted an iCloud link) ── */}
      {user && !hideHybrid && shortcutUrlSimple && (
        <div className="card-recipe mb-10 bg-gradient-to-br from-rf-amber/40 to-rf-amber/15 border-rf-amber" data-testid="ios-recommended-card">
          <div className="flex items-start gap-3 mb-4">
            <Lightning size={28} weight="fill" className="text-rf-orange shrink-0 mt-0.5" />
            <div>
              <p className="overline mb-1">One tap · Zero token · Zero paste</p>
              <h2 className="font-display text-2xl font-black text-rf-ink leading-tight">Install the shortcut.</h2>
            </div>
          </div>
          <p className="text-rf-ink-soft text-sm mb-5 leading-relaxed">
            Tap the button → Shortcuts.app opens → tap <b>Add Shortcut</b>. That&apos;s it. From then on:
          </p>
          <ul className="text-sm text-rf-ink-soft space-y-1.5 mb-5 pl-1">
            <li className="flex gap-2"><span className="text-rf-orange">•</span> <span><b>Safari / TikTok / YouTube</b> — tap Share → <b>Save to Recipe Filer</b>.</span></li>
            <li className="flex gap-2"><span className="text-rf-orange">•</span> <span><b>Instagram Reels</b> — Copy Link → tap the shortcut from your Home Screen or Widget.</span></li>
          </ul>
          <a href={shortcutUrlSimple} target="_blank" rel="noopener noreferrer" data-testid="ios-recommended-install" className="btn-amber w-full justify-center">
            <DownloadSimple size={18} weight="bold" /> Install shortcut
          </a>
          <a href={testShareUrl} target="_blank" rel="noopener noreferrer" data-testid="ios-test-button-top" className="mt-3 text-xs font-bold text-rf-orange hover:underline inline-flex items-center gap-1.5 justify-center w-full">
            <TestTube size={14} weight="bold" /> Test it now with a BBC Good Food recipe
          </a>
        </div>
      )}

      {/* ── Manual 4-action build (only when no iCloud link is hosted yet) ── */}
      {user && !hideHybrid && !shortcutUrlSimple && (
        <>
          <div className="mb-4 flex items-center gap-2">
            <div className="w-8 h-px bg-rf-orange" />
            <p className="overline text-rf-orange">90-second build · 4 actions</p>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-rf-ink mb-3">Build it in two stages.</h2>
          <p className="text-rf-ink-soft mb-8 max-w-2xl">
            Open <b>Shortcuts.app</b> on your iPhone → tap <b>+</b> to create a new shortcut → rename it <span className="font-mono bg-rf-cream-alt px-2 py-0.5 rounded text-sm">Save to Recipe Filer</span>. Then follow the two stages below.
          </p>

          <VisualStage n={1} title="Add the four actions" testId="ios-stage-1" mock={<MockActions />}>
            <p>Tap <b>Add Action</b> and add these four actions in order:</p>
            <ol className="list-decimal ml-4 mt-2 space-y-1.5 text-[13px]">
              <li>
                <b>Get URLs from Input</b> (Web) — this reads the URL the Share Sheet handed us.
                <span className="block mt-1 pl-2 border-l-2 border-rf-orange text-rf-orange">
                  <ClipboardText size={12} weight="bold" className="inline mb-0.5" /> Tap the greyed <b>&ldquo;If there&apos;s no input&rdquo;</b> pill inside the action and change it to <b>Clipboard</b>. This is what makes Instagram Reels work — your Copy-Link becomes the input.
                </span>
              </li>
              <li><b>URL Encode</b> — picks up <span className="font-mono">URLs</span> from step 1 automatically.</li>
              <li>
                <b>Text</b> — paste this exactly, then insert the <b>URL Encoded</b> pill right after <span className="font-mono">?url=</span>:
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-rf-cream-alt rounded-lg p-2 mt-2" data-testid="ios-simple-url-box">
                  <code className="flex-1 font-mono text-[11px] text-rf-ink break-all" data-testid="ios-simple-url">{safariEndpoint}</code>
                  <button onClick={() => copy(safariEndpoint, "URL")} className="btn-outline-ink !py-1 !px-2 !text-xs whitespace-nowrap"><CopyIcon size={12} weight="bold" /> Copy</button>
                </div>
                <span className="block mt-1 text-xs text-rf-orange"><b>⚠ Use the Text action</b>, not URL — the URL action silently drops the query string.</span>
              </li>
              <li><b>Open URLs</b> — auto-picks the Text output. Done.</li>
            </ol>
          </VisualStage>

          <VisualStage n={2} title="Turn on Share Sheet" testId="ios-stage-3" mock={<MockDetails />} final>
            <p>Tap the <b>ⓘ</b> icon at the bottom of the shortcut editor → toggle <b>Show in Share Sheet</b> ON → tap <b>Done</b>.</p>
            <p className="text-xs italic mt-2 text-rf-ink-soft">That&apos;s it. Every URL on your iPhone is one Share-tap (or Copy-Link + shortcut tap) away from your Inbox.</p>
          </VisualStage>

          {/* Try-it-now test card */}
          <div className="card-recipe bg-rf-ink text-rf-cream mb-8 border-rf-ink" data-testid="ios-try-now-card">
            <div className="flex items-start gap-3 mb-3">
              <TestTube size={28} weight="duotone" className="text-rf-amber shrink-0 mt-0.5" />
              <div>
                <p className="overline text-rf-amber mb-1">Try it now</p>
                <h3 className="font-display text-xl font-black">Test that it works end-to-end.</h3>
              </div>
            </div>
            <p className="text-rf-cream/70 text-sm mb-4">Tap the button below on your iPhone — it fires a real BBC Good Food recipe through the same pipeline a share would. If you see the &ldquo;Saved to your Inbox&rdquo; card, you&apos;re golden.</p>
            <a href={testShareUrl} target="_blank" rel="noopener noreferrer" data-testid="ios-test-button" className="btn-amber w-full justify-center">
              <TestTube size={18} weight="bold" /> Test the flow
            </a>
          </div>

          {/* iCloud-link CTA — first user to send us their iCloud link unlocks one-tap install */}
          <div className="card-recipe bg-rf-amber/25 border-rf-amber mb-10" data-testid="ios-community-cta">
            <div className="flex items-start gap-3 mb-3">
              <Envelope size={26} weight="duotone" className="text-rf-orange shrink-0 mt-0.5" />
              <div>
                <p className="overline mb-1">Help us make this one-tap for everyone</p>
                <h3 className="font-display text-xl font-black text-rf-ink">Send us your iCloud link.</h3>
              </div>
            </div>
            <p className="text-rf-ink text-sm leading-relaxed">
              Once your shortcut is built and tested, tap its <b>Share</b> icon inside the editor → <b>Copy iCloud Link</b>.
              Paste that link into an email to <a className="font-bold underline" href="mailto:contactus@recipefiler.com?subject=iCloud%20shortcut%20link">contactus@recipefiler.com</a>
              and we&apos;ll add it here so every future user gets one-tap install.
            </p>
          </div>
        </>
      )}

      {/* ── Instagram gotcha (only shown alongside the hybrid Safari method) ── */}
      {!hideHybrid && (
        <div className="card-recipe bg-rf-cream-alt border-rf-amber/40 mb-10" data-testid="ios-instagram-note">
          <div className="flex items-start gap-3 mb-2">
            <Info size={22} weight="fill" className="text-rf-orange shrink-0 mt-0.5" />
            <p className="font-display font-extrabold text-rf-ink text-lg leading-tight">Instagram &amp; TikTok tip</p>
          </div>
          <p className="text-rf-ink-soft text-sm leading-relaxed">
            Instagram Reels and some TikTok posts refuse to pass a URL to iOS Shortcuts.
            If a share lands you on the <b>Couldn&apos;t import</b> page, tap <b>Copy Link</b> inside the app&apos;s Share menu first,
            then tap the <b>Save to Recipe Filer</b> shortcut from your Home Screen or Widget &mdash; the shortcut&apos;s clipboard fallback will do the rest.
          </p>
        </div>
      )}

      {/* ── Token-path toggle button removed — both methods now render
             simultaneously when the hybrid method is visible. Admin controls
             which methods appear via the "Hide hybrid" toggle in Site Config. ── */}

      {user && showTokenPath && (
        <>
          {shortcutUrl && (
            <div className="card-recipe mb-6 bg-gradient-to-br from-rf-amber/20 to-rf-amber/5 border-rf-amber/60" data-testid="ios-one-tap-card">
              <p className="overline mb-2">To Install</p>
              <h2 className="font-display text-3xl md:text-4xl font-black text-rf-ink leading-tight mb-6">A one-time copy-and-paste unique token inside a Shortcut you create in a couple of clicks on your phone.</h2>
              <p className="text-rf-ink-soft text-sm md:text-base mb-5 leading-relaxed" data-testid="ios-token-steps-paragraph">
                <b>Step 1</b> &mdash; copy your personal token.
                {" "}<b>Step 2</b> &mdash; tap Install.
                {" "}<b>Step 3</b> &mdash; Confirm &ldquo;Save to Recipe Filer&rdquo; Shortcut.
                {" "}<b>Step 4</b> &mdash;
                {" "}<b>a)</b> Inside Shortcuts, tap the new <b>Save to Recipe Filer</b> tile &rarr; top-left <b>3 dots ⋯</b> &rarr; scroll a little to <b>Get contents of</b> &rarr; tap the small <b>chevron</b> (arrowhead in a circle) to expand.
                {" "}<b>b)</b> Scroll to <b>Request Body</b> &rarr; below that, <b>Token</b> &rarr; to its right delete <b>only</b> the wording <span className="font-mono bg-white/70 px-1.5 py-0.5 rounded border border-rf-ink/10 text-[11px]">PASTE_YOUR_TOKEN_HERE</span> (leave the &ldquo;Token&rdquo; label) and paste your copied token in its place.
                {" "}<b>c)</b> Bottom of screen &rarr; tap the little <b>ⓘ</b> &rarr; make sure <b>Show in Share Sheet</b> is ON (green).
                {" "}<b>d)</b> Tap the blue <b>✓</b> at the top, then top-left <b>back arrow</b> to leave the editor.
                {" "}<b>e)</b> Approve any iOS sharing prompts along the way. <b className="text-rf-orange">All done 🎉</b>
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => copy(shareToken, "Token")}
                  disabled={tokenLoading || !shareToken}
                  data-testid="ios-one-tap-copy-token"
                  className="btn-outline-ink flex-1 justify-center disabled:opacity-50"
                >
                  <CopyIcon size={18} weight="bold" /> Step 1: Copy token
                </button>
                <a
                  href={shortcutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ios-one-tap-install"
                  className="btn-amber flex-1 justify-center"
                >
                  <DownloadSimple size={18} weight="bold" /> Step 2: Install shortcut
                </a>
              </div>
            </div>
          )}

          {/* Your token (kept as its own card for clarity + rotation) */}
          <div className="card-recipe mb-6" data-testid="ios-share-token-card">
            <p className="overline text-rf-orange mb-1">Your personal share token</p>
            <p className="text-rf-ink-soft text-sm leading-relaxed mb-3">Keep this private &mdash; anyone with it can save recipes into your Inbox.</p>
            {tokenLoading && !shareToken && <p className="text-sm text-rf-ink-soft italic mt-3">Loading your token…</p>}
            {shareToken && (
              <>
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-rf-cream-alt rounded-xl p-3 mt-3" data-testid="ios-token-box">
                  <code className="flex-1 font-mono text-xs text-rf-ink break-all" data-testid="ios-share-token">{shareToken}</code>
                  <button onClick={() => copy(shareToken, "Token")} data-testid="ios-token-copy" className="btn-amber !py-2 !px-3 !text-sm whitespace-nowrap inline-flex items-center gap-1.5">
                    <CopyIcon size={14} weight="bold" /> Copy
                  </button>
                </div>
                <button onClick={rotateToken} disabled={tokenLoading} data-testid="ios-token-rotate" className="mt-3 text-xs text-rf-ink-soft hover:text-rf-orange inline-flex items-center gap-1.5 disabled:opacity-50">
                  <ArrowsClockwise size={12} weight="bold" /> Rotate token (revoke previous device)
                </button>
              </>
            )}
          </div>

          {/* How to Use — daily flow after the shortcut is installed */}
          <div className="mb-8" data-testid="ios-how-to-use">
            <p className="overline text-rf-orange mb-3">How to Use</p>
            <h2 className="font-display text-3xl md:text-4xl font-black text-rf-ink mb-6 leading-tight">Once installed, here&apos;s the daily flow.</h2>
            <div className="space-y-4 text-rf-ink-soft leading-relaxed">
              <p>
                When you find a website or social post with a recipe on it, tap the <b>Share</b> icon (the arrowhead-and-square). Find the <b>Share to&hellip;</b> row, tap it, scroll all the way down to <b>Save to Recipe Filer</b>, and tap.
              </p>
              <p>
                In about 30 seconds it should appear in your{" "}
                <Link to="/dashboard" className="text-rf-orange font-bold hover:underline">RecipeFiler.com Inbox cookbook</Link>{" "}
                for you to view and/or edit.
              </p>
              <p>
                The best recipes are the ones with itemised ingredients and cooking steps. The majority will save cleanly, but if one doesn&apos;t you can always copy the page or post&apos;s link and add it to any of your cookbooks that way.
              </p>
              <p>
                Occasionally it fails because the page just doesn&apos;t have enough for us to work with. But that&apos;s just how the cookie crumbles sometimes 🙂
              </p>
              <p className="font-display font-black text-rf-ink text-lg pt-2">
                Have fun collecting and creating great recipe books!
              </p>
            </div>
          </div>

          {/* Manual token build — always visible in the token-only tutorial */}
          <div className="card-recipe" data-testid="ios-token-build">
            <p className="font-display font-extrabold text-rf-ink mb-3 text-lg">Manual token build</p>
            <ol className="list-decimal ml-5 space-y-1 text-sm text-rf-ink-soft">
              <li>Open Shortcuts → tap <b>+</b> → rename <span className="font-mono bg-rf-cream-alt px-1.5 py-0.5 rounded">Save to Recipe Filer</span>.</li>
              <li>Add action <b>Get URLs from Input</b>.</li>
              <li>Add action <b>Get Contents of URL</b> — URL: <span className="font-mono text-xs bg-rf-cream-alt px-1 py-0.5 rounded">{apiEndpointText}</span> — Method: POST — Headers: <span className="font-mono text-xs">Content-Type: application/json</span> — Body: JSON with <span className="font-mono">token</span> and <span className="font-mono">url</span>.</li>
              <li>Add action <b>Show Notification</b> — set the message to <b>Contents of URL</b>. (This is the fix for the &quot;share unsuccessful&quot; iOS banner — see the yellow box above.)</li>
              <li>ⓘ → toggle <b>Show in Share Sheet</b> ON. Done.</li>
            </ol>
          </div>

          {/* Troubleshooter for the "share unsuccessful" banner people report. */}
          <div className="card-recipe mt-6 bg-rf-amber/20 border-rf-amber/60" data-testid="ios-share-unsuccessful-troubleshooter">
            <p className="font-display font-extrabold text-rf-ink mb-2 text-lg">Seeing &quot;share unsuccessful&quot; on iOS?</p>
            <p className="text-sm text-rf-ink-soft leading-relaxed">
              That&apos;s an iOS Share Sheet quirk — the extension has a ~10s budget. Recipe Filer used to inline the scrape + parse which sometimes ran longer, so iOS killed the extension and popped a red banner even though the recipe landed in your Inbox correctly.
            </p>
            <p className="text-sm text-rf-ink-soft leading-relaxed mt-3">
              <b>Fixed as of Feb 2026</b> — the endpoint now returns instantly and processes in the background. To pick up the fix, rebuild your Shortcut with these two tweaks:
            </p>
            <ol className="list-decimal ml-5 mt-2 space-y-1 text-sm text-rf-ink-soft">
              <li>
                Update the <b>Get Contents of URL</b> action to point at{" "}
                <button
                  onClick={() => copy(apiEndpointText, "endpoint URL")}
                  className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded hover:bg-white transition-colors underline decoration-dashed"
                  data-testid="copy-text-endpoint"
                >{apiEndpointText}</button>
                {" "}(the <span className="font-mono">?format=text</span> tells the endpoint to return plain text your Shortcut can display).
              </li>
              <li>Add a final <b>Show Notification</b> action with the message set to <b>Contents of URL</b>. This gives iOS an explicit output signal → no more &quot;unsuccessful&quot; banner.</li>
            </ol>
          </div>
        </>
      )}

      <div className="card-recipe mt-6 text-center bg-rf-ink text-rf-cream" data-testid="ios-footer-card">
        <CheckCircle size={48} weight="duotone" className="mx-auto text-rf-amber mb-3" />
        <p className="font-display font-black text-2xl mb-2">System-level share, forever.</p>
        <p className="text-rf-cream/70 text-sm max-w-md mx-auto">
          Open any recipe in TikTok / Safari → tap <b>Share</b> → scroll → tap
          <span className="inline-flex items-center gap-1 mx-1 align-middle px-2 py-0.5 rounded-md bg-rf-amber/30 text-rf-cream font-bold text-xs">
            <ShareNetwork size={12} weight="bold" /> Save to Recipe Filer
          </span>.
        </p>
      </div>
    </div>
  );
}

/* VisualStage — one row of {big number pill} + {mock illustration on the right,
   copy on the left}. Stacks vertically on mobile so the mock is always visible
   under the instructions. */
function VisualStage({ n, title, testId, mock, final, children }) {
  return (
    <div className="card-recipe mb-4" data-testid={testId}>
      <div className="grid md:grid-cols-[auto_1fr_auto] gap-4 md:gap-6 items-start">
        <div className={`w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center font-display font-black text-2xl ${final ? "bg-rf-orange text-white" : "bg-rf-amber text-rf-ink"}`}>
          {final ? <CheckCircle size={26} weight="fill" /> : n}
        </div>
        <div className="min-w-0">
          <p className="font-display font-extrabold text-rf-ink text-xl mb-2 leading-tight">{title}</p>
          <div className="text-sm text-rf-ink-soft space-y-1 leading-relaxed">
            {children}
          </div>
        </div>
        <div className="md:col-start-3 md:row-start-1 md:row-span-2 order-first md:order-none">
          <div className="bg-rf-cream-alt rounded-2xl p-3 border border-rf-ink/5">
            {mock}
          </div>
        </div>
      </div>
    </div>
  );
}
